import { useState } from "react";
import {
  Play,
  RotateCcw,
  SlidersHorizontal,
  Loader2,
  CheckCircle2,
  ListFilter,
  Eye,
  EyeOff,
  Check,
  RefreshCw,
  Lock,
  LockOpen,
} from "lucide-react";
import { useAppStore } from "@/store/useAppStore";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SectionHeading, EmptyState } from "@/components/ui/misc";
import { ParameterControl } from "./ParameterControl";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils/cn";

/**
 * Section 1 — Parameter Controls.
 *
 * Renders a control per exposed parameter, accumulates a deferred draft, and
 * applies everything at once via the Go button. The Go button is disabled while
 * validation fails or nothing has changed, mirroring native Apple form ergonomics.
 */
export function ParameterPanel() {
  const allDescriptors = useAppStore((s) => s.allDescriptors);
  const descriptors = useAppStore((s) => s.descriptors);
  const exposedIds = useAppStore((s) => s.config.exposedParameterIds);
  const draft = useAppStore((s) => s.draft);
  const validation = useAppStore((s) => s.validation);
  const isDirty = useAppStore((s) => s.isDirty);
  const isApplying = useAppStore((s) => s.isApplying);
  const setDraftValue = useAppStore((s) => s.setDraftValue);
  const resetDraft = useAppStore((s) => s.resetDraft);
  const executeGo = useAppStore((s) => s.executeGo);
  const permission = useAppStore((s) => s.config.permission);
  const setParameterIncluded = useAppStore((s) => s.setParameterIncluded);
  const includeAllParameters = useAppStore((s) => s.includeAllParameters);
  const reloadConfiguration = useAppStore((s) => s.reloadConfiguration);
  const updateConfig = useAppStore((s) => s.updateConfig);
  const isLocked = useAppStore((s) => s.config.configLocked);
  const tableau = useAppStore((s) => s.tableau);

  const [manageMode, setManageMode] = useState(false);
  const [reloadOpen, setReloadOpen] = useState(false);

  // Value entry is always available — the whole point of the extension. The
  // admin tools (Manage / Lock / Reload) are for workbook authors only: they
  // require admin permission AND Tableau's authoring mode (Desktop / web
  // edit), so viewers on Server/Cloud never see them.
  const isAuthoring = tableau?.mode === "authoring" || tableau?.mode === "dev";
  const canManage = isAuthoring && permission === "admin";
  const errorCount = validation.errors.length;
  const isIncluded = (id: string) => exposedIds.length === 0 || exposedIds.includes(id);

  async function handleReload(keepData: boolean) {
    setReloadOpen(false);
    await reloadConfiguration(keepData);
    setManageMode(false);
    toast.info(
      "Configuration reloaded",
      keepData
        ? "Parameters refreshed — your setup and history were kept."
        : "Started from scratch — setup and history were reset.",
    );
  }

  async function handleToggleLock() {
    const next = !isLocked;
    if (next) setManageMode(false); // can't keep editing while locked
    await updateConfig({ configLocked: next });
    toast.info(
      next ? "Parameter setup locked" : "Parameter setup unlocked",
      next
        ? "Enabling/disabling parameters is now disabled. Value controls stay active."
        : "You can enable or disable parameters again.",
    );
  }

  async function handleGo() {
    if (!validation.valid) {
      toast.error("Fix validation errors", `${errorCount} field(s) need attention.`);
      return;
    }
    await executeGo();
    toast.success("Parameters applied", "Dashboard refreshed with your selections.");
  }

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <SectionHeading
          icon={SlidersHorizontal}
          title="Parameter Controls"
          subtitle="Edit freely — nothing applies until you press Go"
          action={
            <div className="flex items-center gap-2">
              {errorCount > 0 ? (
                <Badge variant="destructive">{errorCount} issue{errorCount > 1 ? "s" : ""}</Badge>
              ) : isDirty ? (
                <Badge variant="warning">Unsaved</Badge>
              ) : (
                <Badge variant="success" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Applied
                </Badge>
              )}
              {canManage && (
                <>
                  {/* Manage = enable/disable which parameters appear. Disabled
                      while the setup is locked. Value controls are unaffected. */}
                  <Button
                    variant={manageMode ? "default" : "outline"}
                    size="sm"
                    disabled={isLocked}
                    onClick={() => setManageMode((m) => !m)}
                    title={isLocked ? "Unlock to enable or disable parameters" : "Enable or disable parameters"}
                  >
                    {manageMode ? <Check className="h-4 w-4" /> : <ListFilter className="h-4 w-4" />}
                    {manageMode ? "Done" : "Manage"}
                  </Button>
                  <Button
                    variant={isLocked ? "secondary" : "ghost"}
                    size="sm"
                    onClick={handleToggleLock}
                    title={
                      isLocked
                        ? "Unlock to allow enabling/disabling parameters"
                        : "Lock the parameter setup so it can't be changed"
                    }
                  >
                    {isLocked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
                    {isLocked ? "Locked" : "Lock"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReloadOpen(true)}
                    title="Re-read parameters from the dashboard — keep your data or start from scratch"
                  >
                    <RefreshCw className="h-4 w-4" /> Reload
                  </Button>
                </>
              )}
            </div>
          }
        />
      </CardHeader>

      <CardContent className="flex max-h-[62vh] flex-col gap-3 overflow-y-auto scrollbar-thin">
        {manageMode ? (
          <ManageParameters
            allDescriptors={allDescriptors}
            isIncluded={isIncluded}
            includedCount={descriptors.length}
            onToggle={(id, on) => setParameterIncluded(id, on)}
            onIncludeAll={() => includeAllParameters()}
          />
        ) : descriptors.length === 0 ? (
          <EmptyState
            icon={SlidersHorizontal}
            title={
              allDescriptors.length === 0
                ? "No parameters found in this dashboard"
                : "All parameters are hidden"
            }
            description={
              allDescriptors.length === 0
                ? "This extension reads workbook parameters. Add at least one parameter to the workbook, then reopen the dashboard."
                : "Use Manage to include the parameters you want to control."
            }
            className="my-6"
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {descriptors.map((d) => (
              <ParameterControl
                key={d.id}
                descriptor={d}
                value={draft[d.id] ?? null}
                errors={validation.byParameter[d.id] ?? []}
                onChange={(v) => setDraftValue(d.id, v)}
                disabled={isApplying}
              />
            ))}
          </div>
        )}
      </CardContent>

      <div className="flex items-center gap-2 border-t border-border/60 p-3">
        <Button
          size="lg"
          className="flex-1"
          disabled={!validation.valid || isApplying || !isDirty}
          onClick={handleGo}
        >
          {isApplying ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          {isApplying ? "Applying…" : "Go"}
        </Button>

        <Button
          variant="outline"
          size="lg"
          onClick={resetDraft}
          disabled={!isDirty || isApplying}
          aria-label="Reset to applied values"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>

      {/* Reload choice — refresh the parameter list either keeping the saved
          setup + usage history, or wiping everything and starting over. */}
      <Dialog open={reloadOpen} onOpenChange={setReloadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reload configuration</DialogTitle>
            <DialogDescription>
              Parameters are re-read from the dashboard either way. Choose what
              happens to your existing setup and Top Combinations history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="destructive" onClick={() => handleReload(false)}>
              Start from scratch
            </Button>
            <Button onClick={() => handleReload(true)}>
              Use existing data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

interface ManageParametersProps {
  allDescriptors: { id: string; name: string; controlType: string }[];
  isIncluded: (id: string) => boolean;
  includedCount: number;
  onToggle: (id: string, included: boolean) => void;
  onIncludeAll: () => void;
}

/**
 * Inline include/exclude editor shown in the Parameter Controls panel when
 * "Manage" is active. Lists every parameter discovered in the workbook with a
 * toggle; excluded ones are dimmed and hidden from the live controls.
 */
function ManageParameters({
  allDescriptors,
  isIncluded,
  includedCount,
  onToggle,
  onIncludeAll,
}: ManageParametersProps) {
  return (
    <div className="space-y-3 animate-fade-in">
      <div className="flex items-center justify-between gap-2 rounded-lg bg-secondary/50 px-3 py-2">
        <p className="text-xs text-muted-foreground">
          Choose which dashboard parameters to control.{" "}
          <span className="font-medium text-foreground">
            {includedCount} of {allDescriptors.length}
          </span>{" "}
          included. Saved &amp; locked to the workbook automatically.
        </p>
        <Button variant="ghost" size="sm" onClick={onIncludeAll}>
          Include all
        </Button>
      </div>

      <div className="space-y-2">
        {allDescriptors.map((d) => {
          const included = isIncluded(d.id);
          return (
            <label
              key={d.id}
              className={cn(
                "glass-sm flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5 transition-opacity",
                !included && "opacity-55",
              )}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                {included ? (
                  <Eye className="h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <EyeOff className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium" title={d.name}>
                    {d.name}
                  </p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                    {d.controlType.replace("-", " ")}
                  </p>
                </div>
              </div>
              <Switch
                checked={included}
                onCheckedChange={(v) => onToggle(d.id, v)}
                aria-label={`${included ? "Exclude" : "Include"} ${d.name}`}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
